import React, { useState, useMemo } from 'react';
import { useQuery, useAction } from 'wasp/client/operations';
import { getBuySells, deleteBuySell } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { DashboardLayout } from '../../client/components/Sidebar';
import { useLocationContext } from '../../client/contexts/LocationContext';
import { resolveLocationSlug, locationSectionPath } from '../../client/lib/locationPaths';
import { isAdministrator } from '../../client/lib/platformRole';
import {
  EnhancedActionButton,
  EnhancedIconButton,
  enhancedPageLayoutStyles,
  useResponsiveLayout
} from '../../components/ui/enhanced-page-layout';
import { Search, Filter, Plus, Eye, Edit, Trash2, DollarSign, Tag, Phone, Grid3X3, List, Table } from 'lucide-react';
import { withSectionProtection } from '../../client/components/ProtectedSection';
import { useFixedPageStack } from '../../client/hooks/useFixedPageStack';

function BuySellPage() {
  const { data: user } = useAuth();
  const showAdminColumns = isAdministrator(user);
  const { getLocationIdsForQuery, selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const params = useParams<{ slug?: string }>();
  const routerLocation = useLocation();
  const locSlug = resolveLocationSlug(routerLocation.pathname, params.slug, selectedLocation);
  const deleteBuySellFn = useAction(deleteBuySell);
  
  // State for filters and view mode
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'title' | 'price'>('newest');
  const [showFilters, setShowFilters] = useState(false);
  // Use responsive layout hook
  const { viewMode, setViewMode, isMobile, shouldCollapseSidebar, hideViewSwitcher } = useResponsiveLayout('table');

  // Fetch items with hub aggregation support
  const { data: items = [], isLoading, error } = useQuery(getBuySells, {
    locationIds: getLocationIdsForQuery(),
    filters: {
      type: 'BUY_SELL',
    }
  });

  const hasListContent = !isLoading && !error && items.length > 0;
  const fixedStack = useFixedPageStack({
    secondaryKind: hasListContent ? 'ref' : 'none',
    secondaryRefDeps: [viewMode, items.length, isLoading, error, showFilters],
  });

  // Filter and sort items
  const filteredItems = useMemo(() => {
    let filtered = items as any[];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter((item: any) =>
        item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter((item: any) => 
        item.metadata?.category === selectedCategory
      );
    }

    // Sort
    switch (sortBy) {
      case 'newest':
        filtered = [...filtered].sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      case 'oldest':
        filtered = [...filtered].sort((a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        break;
      case 'title':
        filtered = [...filtered].sort((a: any, b: any) => a.title.localeCompare(b.title));
        break;
      case 'price':
        filtered = [...filtered].sort((a: any, b: any) => (b.metadata?.price || 0) - (a.metadata?.price || 0));
        break;
    }

    return filtered;
  }, [items, searchTerm, selectedCategory, sortBy]);

  const handleDelete = async (itemId: string) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await deleteBuySellFn({ itemId });
        // The query will automatically refetch due to Wasp's reactivity
      } catch (error) {
        console.error('Error deleting item:', error);
        alert('Error deleting item. Please try again.');
      }
    }
  };

  if (!selectedLocation) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">No Location Selected</h2>
            <p className="text-gray-600">Please select a location to view buy/sell items.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const getCategoryColor = (category: string) => {
    return category === 'BUY' ? 'bg-success/10 text-success border border-success/20' : 'bg-primary/10 text-primary border border-primary/20';
  };

  const getConditionColor = (condition: string) => {
    const colors = {
      'NEW': 'bg-success/10 text-success border border-success/20',
      'LIKE_NEW': 'bg-primary/10 text-primary border border-primary/20',
      'GOOD': 'bg-warning/10 text-warning border border-warning/20',
      'FAIR': 'bg-secondary/10 text-secondary border border-secondary/20',
      'POOR': 'bg-destructive/10 text-destructive border border-destructive/20'
    };
    return colors[condition as keyof typeof colors] || 'bg-muted text-muted-foreground border border-border';
  };

  return (
    <DashboardLayout 
      forceSidebarCollapse={shouldCollapseSidebar}
      showViewSwitcher={!hideViewSwitcher}
      activeView={viewMode}
      onViewChange={setViewMode}
    >
      <div className={fixedStack.pageContainerClassName} style={fixedStack.pageContainerStyle}>
        {/* Fixed Main Header */}
        <div
          ref={fixedStack.pageHeaderRef}
          className="fixed top-[var(--header-height)] left-0 lg:left-[var(--sidebar-width)] right-0 z-20 bg-white border-b border-gray-200 transition-all duration-300"
        >
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Buy & Sell</h1>
                <p className="text-sm text-gray-600 mt-1">Buy and sell items within {selectedLocation.name} ({filteredItems.length} items)</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowFilters(f => !f)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    showFilters
                      ? 'bg-blue-50 border-blue-300 text-blue-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  <span className="hidden lg:inline">Filters</span>
                </button>
                {user && (
                  <EnhancedActionButton
                    onClick={() => navigate(locationSectionPath(locSlug, 'buy-sell', 'add'))}
                    icon={Plus}
                    label={<span className="hidden lg:inline">Post Item</span>}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Fixed Table Header (Conditional) */}
        {!isLoading && !error && items.length > 0 && viewMode === 'table' && (
          <div
            ref={fixedStack.secondaryStackRef}
            className={`fixed ${fixedStack.secondaryFixedTopClass} left-0 lg:left-[var(--sidebar-width)] right-0 z-10 bg-sidebar-hover border-b border-border transition-all duration-300`}
          >
            <div className="px-6 py-3 space-y-3">
              {/* Column Headers */}
              <div className="grid grid-cols-12 gap-4 text-xs font-medium text-content-text-muted uppercase tracking-wider select-none cursor-default">
                <div className="col-span-3">Item</div>
                <div className="col-span-2">Category</div>
                <div className="col-span-1">Price</div>
                <div className="col-span-2">Condition</div>
                <div className="col-span-2">Author</div>
                <div className={showAdminColumns ? 'col-span-1' : 'col-span-2'}>Date</div>
                {showAdminColumns && <div className="col-span-1">Actions</div>}
              </div>

              {/* Column Filters */}
              {showFilters && <div className="grid grid-cols-12 gap-4">
                <div className="col-span-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search items..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div className="col-span-2">
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All</option>
                    <option value="BUY">Want to Buy</option>
                    <option value="SELL">Want to Sell</option>
                  </select>
                </div>
                <div className="col-span-1">
                  <select
                    value={sortBy === 'price' ? 'price' : 'all'}
                    onChange={(e) => setSortBy(e.target.value === 'price' ? 'price' : 'newest')}
                    className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All</option>
                    <option value="price">High-Low</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <input
                    type="text"
                    placeholder="All"
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="col-span-2">
                  <input
                    type="text"
                    placeholder="All"
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className={showAdminColumns ? 'col-span-1' : 'col-span-2'}>
                  <select
                    value={sortBy === 'newest' || sortBy === 'oldest' ? sortBy : 'newest'}
                    onChange={(e) => setSortBy(e.target.value as any)}
                    className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="newest">Newest</option>
                    <option value="oldest">Oldest</option>
                    <option value="title">A-Z</option>
                  </select>
                </div>
                {showAdminColumns && (
                  <div className="col-span-1">
                    {/* Actions column - no filter needed */}
                  </div>
                )}
              </div>}
            </div>
          </div>
        )}

        {/* Fixed Filters for non-table views */}
        {!isLoading && !error && items.length > 0 && viewMode !== 'table' && (
          <div
            ref={fixedStack.secondaryStackRef}
            className={`fixed ${fixedStack.secondaryFixedTopClass} left-0 lg:left-[var(--sidebar-width)] right-0 z-10 bg-white border-b border-gray-200 transition-all duration-300`}
          >
            <div className="px-4 py-3">
              <div className="flex items-center gap-4">
                {/* Search */}
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search items..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Category Filter */}
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Categories</option>
                  <option value="BUY">Want to Buy</option>
                  <option value="SELL">Want to Sell</option>
                </select>
                
                {/* Sort Filter */}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="title">Title A-Z</option>
                  <option value="price">Price High-Low</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Scrollable Content */}
        <div
          className={fixedStack.scrollContainerClassName}
          style={fixedStack.scrollContainerStyle}
        >
          <div className="w-full">
            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-600 mt-2">Loading items...</p>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <DollarSign className="w-12 h-12 text-red-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Items</h3>
                  <p className="text-gray-600">Please try again later.</p>
                </div>
              </div>
            )}

            {/* Empty State */}
            {!isLoading && !error && filteredItems.length === 0 && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Items Found</h3>
                  <p className="text-gray-600">
                    {searchTerm || selectedCategory !== 'all' 
                      ? 'Try adjusting your search or filters to find what you\'re looking for.' 
                      : 'Be the first to post an item in this location!'
                    }
                  </p>
                </div>
              </div>
            )}

        {/* Content */}
        {!isLoading && !error && filteredItems.length > 0 && (
          <>
            {/* Cards View - Use enhanced layout with padding */}
            {viewMode === 'cards' && (
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredItems.map((item) => (
                    <div
                      key={item.id}
                      className={`${enhancedPageLayoutStyles.card.container} cursor-pointer hover:shadow-md transition-shadow`}
                      onClick={() => navigate(locationSectionPath(locSlug, 'buy-sell', item.id))}
                    >
                      <div className={enhancedPageLayoutStyles.card.content}>
                        <div className={enhancedPageLayoutStyles.card.header}>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 text-xs font-medium rounded ${getCategoryColor(item.metadata?.category)}`}>
                              {item.metadata?.category}
                            </span>
                            {item.metadata?.condition && (
                              <span className={`px-2 py-1 text-xs font-medium rounded ${getConditionColor(item.metadata.condition)}`}>
                                {item.metadata.condition.replace('_', ' ')}
                              </span>
                            )}
                          </div>
                          {item.metadata?.price && (
                            <div className="text-lg font-bold text-success">
                              ${item.metadata.price}
                            </div>
                          )}
                        </div>
                        
                        <h3 className={enhancedPageLayoutStyles.card.title}>
                          {item.title}
                        </h3>
                        
                        <p className={enhancedPageLayoutStyles.card.description}>
                          {item.description}
                        </p>
                        
                        <div className={enhancedPageLayoutStyles.card.footer}>
                          <div className={enhancedPageLayoutStyles.card.meta}>
                            <span className="text-sm text-content-text-muted">
                              by {item.author.name || item.author.email}
                            </span>
                          </div>
                          <div 
                            className={enhancedPageLayoutStyles.card.actions}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <EnhancedIconButton
                              onClick={() => navigate(locationSectionPath(locSlug, 'buy-sell', item.id))}
                              icon={Eye}
                              title="View details"
                            />
                            {user && item.authorId === user.id && (
                              <>
                                <EnhancedIconButton
                                  onClick={() => navigate(locationSectionPath(locSlug, 'buy-sell', item.id, 'edit'))}
                                  icon={Edit}
                                  title="Edit item"
                                />
                                <EnhancedIconButton
                                  onClick={() => handleDelete(item.id)}
                                  icon={Trash2}
                                  title="Delete item"
                                  variant="danger"
                                />
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* List View - Use enhanced layout with padding */}
            {viewMode === 'list' && (
              <div className="p-6">
                <div className="space-y-4">
                  {filteredItems.map((item) => (
                    <div
                      key={item.id}
                      className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer hover:bg-hover transition-colors"
                      onClick={() => navigate(locationSectionPath(locSlug, 'buy-sell', item.id))}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-gray-900 text-xl">{item.title}</h3>
                            <span className={`px-2 py-1 text-xs font-medium rounded ${getCategoryColor(item.metadata?.category)}`}>
                              {item.metadata?.category}
                            </span>
                            {item.metadata?.condition && (
                              <span className={`px-2 py-1 text-xs font-medium rounded ${getConditionColor(item.metadata.condition)}`}>
                                {item.metadata.condition.replace('_', ' ')}
                              </span>
                            )}
                          </div>
                          
                          <p className="text-gray-600 mb-3 line-clamp-2">{item.description}</p>
                          
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span>by {item.author.name || item.author.email}</span>
                            <span>{new Date(item.createdAt).toLocaleDateString()}</span>
                            {item.metadata?.contactInfo && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {item.metadata.contactInfo}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          {item.metadata?.price && (
                            <div className="text-xl font-bold text-success mr-4">
                              ${item.metadata.price}
                            </div>
                          )}
                          <div 
                            className="flex items-center gap-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <EnhancedIconButton
                              onClick={() => navigate(locationSectionPath(locSlug, 'buy-sell', item.id))}
                              icon={Eye}
                              title="View details"
                            />
                            {user && item.authorId === user.id && (
                              <>
                                <EnhancedIconButton
                                  onClick={() => navigate(locationSectionPath(locSlug, 'buy-sell', item.id, 'edit'))}
                                  icon={Edit}
                                  title="Edit item"
                                />
                                <EnhancedIconButton
                                  onClick={() => handleDelete(item.id)}
                                  icon={Trash2}
                                  title="Delete item"
                                  variant="danger"
                                />
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Table View */}
            {viewMode === 'table' && (
              <div className="w-full">
                {/* Table Body - No internal scroll */}
                <div className="divide-y divide-gray-200">
                  {filteredItems.map((item) => (
                    <div 
                      key={item.id} 
                      className="px-6 py-3 hover:bg-hover transition-colors cursor-pointer"
                      onClick={() => navigate(locationSectionPath(locSlug, 'buy-sell', item.id))}
                    >
                      <div className="grid grid-cols-12 gap-4 items-start">
                        <div className="col-span-3">
                          <div className="text-sm font-medium text-gray-900">
                            {item.title}
                          </div>
                          <div className="text-sm text-gray-500 mt-1 line-clamp-2">
                            {item.description}
                          </div>
                        </div>
                        <div className="col-span-2">
                          <span className="text-sm text-gray-600">
                            {item.metadata?.category}
                          </span>
                        </div>
                        <div className="col-span-1">
                          <span className="text-sm text-gray-600">
                            {item.metadata?.price ? `$${item.metadata.price}` : '-'}
                          </span>
                        </div>
                        <div className="col-span-2">
                          <span className="text-sm text-gray-600">
                            {item.metadata?.condition ? item.metadata.condition.replace('_', ' ') : '-'}
                          </span>
                        </div>
                        <div className="col-span-2">
                          <span className="text-sm text-gray-500">
                            {item.author.name || item.author.email}
                          </span>
                        </div>
                        <div className={showAdminColumns ? 'col-span-1' : 'col-span-2'}>
                          <span className="text-sm text-gray-500">
                            {new Date(item.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        {showAdminColumns && (
                          <div className="col-span-1">
                            <div 
                              className="flex items-center gap-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <EnhancedIconButton
                                onClick={() => navigate(locationSectionPath(locSlug, 'buy-sell', item.id))}
                                icon={Eye}
                                title="View details"
                              />
                              {user && item.authorId === user.id && (
                                <>
                                  <EnhancedIconButton
                                    onClick={() => navigate(locationSectionPath(locSlug, 'buy-sell', item.id, 'edit'))}
                                    icon={Edit}
                                    title="Edit item"
                                  />
                                  <EnhancedIconButton
                                    onClick={() => handleDelete(item.id)}
                                    icon={Trash2}
                                    title="Delete item"
                                    variant="danger"
                                  />
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default withSectionProtection(BuySellPage, 'buy-sell');



